import { useMemo, useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '../../utils';

interface StarRatingProps {
    value: number;
    onChange?: (nextValue: number) => void;
    size?: 'sm' | 'md' | 'lg';
    readOnly?: boolean;
    precision?: 0.1 | 0.5 | 1;
    className?: string;
    ariaHidden?: boolean;
}

const starIconSizes = {
    sm: 'size-4',
    md: 'size-5',
    lg: 'size-7',
} as const;

const interactiveButtonSizes = {
    sm: 'size-9',
    md: 'size-11',
    lg: 'size-12',
} as const;

const starGapSizes = {
    sm: 'gap-0.5',
    md: 'gap-1',
    lg: 'gap-1.5',
} as const;

function clampRating(value: number): number {
    if (!Number.isFinite(value)) {
        return 0;
    }

    return Math.min(5, Math.max(0, value));
}

function normalizeDisplayValue(value: number, precision: 0.1 | 0.5 | 1): number {
    const clamped = clampRating(value);

    if (precision === 0.5) {
        return Math.round(clamped * 2) / 2;
    }

    if (precision === 0.1) {
        return Math.round(clamped * 10) / 10;
    }

    return Math.round(clamped);
}

function formatAriaValue(value: number): string {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function ReadOnlyStars({
    value,
    precision,
    size,
    className,
    ariaHidden,
}: Pick<StarRatingProps, 'value' | 'precision' | 'size' | 'className' | 'ariaHidden'>) {
    const normalizedValue = normalizeDisplayValue(value, precision ?? 0.5);

    return (
        <div
            className={cn('inline-flex items-center', starGapSizes[size ?? 'md'], className)}
            role={ariaHidden ? undefined : 'img'}
            aria-label={ariaHidden ? undefined : `${formatAriaValue(normalizedValue)} de 5 estrellas`}
            aria-hidden={ariaHidden || undefined}
        >
            {Array.from({ length: 5 }, (_, index) => {
                const fillRatio = Math.max(0, Math.min(1, normalizedValue - index));

                return (
                    <span key={index} className="relative inline-flex">
                        <Star
                            aria-hidden="true"
                            className={cn(starIconSizes[size ?? 'md'], 'text-border')}
                        />
                        <span
                            className="absolute inset-y-0 left-0 overflow-hidden text-warning"
                            style={{ width: `${fillRatio * 100}%` }}
                            aria-hidden="true"
                        >
                            <Star className={cn(starIconSizes[size ?? 'md'], 'fill-current')} />
                        </span>
                    </span>
                );
            })}
        </div>
    );
}

export function StarRating({
    value,
    onChange,
    size = 'md',
    readOnly = false,
    precision = 1,
    className,
    ariaHidden = false,
}: StarRatingProps) {
    const [hoverValue, setHoverValue] = useState<number | null>(null);
    const normalizedValue = useMemo(() => clampRating(value), [value]);

    if (readOnly) {
        return (
            <ReadOnlyStars
                value={normalizedValue}
                precision={precision}
                size={size}
                className={className}
                ariaHidden={ariaHidden}
            />
        );
    }

    const previewValue = hoverValue ?? normalizedValue;
    const selectedValue = Math.round(normalizedValue);

    return (
        <div
            className={cn('inline-flex items-center', starGapSizes[size], className)}
            role="radiogroup"
            aria-label="Calificación en estrellas"
            onMouseLeave={() => setHoverValue(null)}
            onKeyDown={(event) => {
                if (!onChange) {
                    return;
                }

                let nextValue: number | null = null;

                if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
                    nextValue = Math.min(5, selectedValue + 1);
                }

                if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
                    nextValue = Math.max(1, selectedValue - 1 || 1);
                }

                if (event.key === 'Home') {
                    nextValue = 1;
                }

                if (event.key === 'End') {
                    nextValue = 5;
                }

                if (nextValue === null) {
                    return;
                }

                event.preventDefault();
                onChange(nextValue);
            }}
        >
            {Array.from({ length: 5 }, (_, index) => {
                const starValue = index + 1;
                const isFilled = previewValue >= starValue;
                const isChecked = selectedValue === starValue;

                return (
                    <button
                        key={starValue}
                        type="button"
                        role="radio"
                        aria-checked={isChecked}
                        aria-label={`${starValue} estrellas`}
                        tabIndex={isChecked || (selectedValue === 0 && starValue === 1) ? 0 : -1}
                        className={cn(
                            'touch-manipulation inline-flex items-center justify-center rounded-full transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35',
                            interactiveButtonSizes[size],
                            isFilled ? 'text-warning' : 'text-border hover:text-warning/70',
                        )}
                        onMouseEnter={() => setHoverValue(starValue)}
                        onBlur={() => setHoverValue(null)}
                        onClick={() => onChange?.(starValue)}
                    >
                        <Star
                            aria-hidden="true"
                            className={cn(
                                starIconSizes[size],
                                isFilled && 'fill-current',
                            )}
                        />
                    </button>
                );
            })}
        </div>
    );
}
